import * as XLSX from 'xlsx';

export interface ParsedQuestion {
  id?: string;
  text: string;
  type: string;
  options: string[];
  isRequired?: boolean;
}

export interface ParsedSubsection {
  title: string;
  questions: ParsedQuestion[];
}

export interface ParsedSection {
  title: string;
  subsections: ParsedSubsection[];
}

export interface ParsedTemplate {
  name: string;
  description: string;
  applicability: 'Both' | 'AI' | 'Dataset';
  sections: ParsedSection[];
}

const generateQuestionId = (
  section: string,
  subsection: string,
  questionText: string
): string => {
  const baseId = `${section.toLowerCase().replace(/\s+/g, '_')}_${subsection
    .toLowerCase()
    .replace(/\s+/g, '_')}_`;

  const hash = questionText
    .substring(0, 20)
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

  return `${baseId}${hash}`;
};

const validateApplicability = (value: string): 'Both' | 'AI' | 'Dataset' => {
  const normalized = value?.trim().toLowerCase() || '';

  if (normalized === 'ai') return 'AI';
  if (normalized === 'dataset') return 'Dataset';

  return 'Both';
};

const parseTemplateData = (
  templateRows: Record<string, any>[],
  questionRows: Record<string, any>[]
): ParsedTemplate => {
  if (!templateRows.length) {
    throw new Error('Templates sheet is empty');
  }

  if (!questionRows.length) {
    throw new Error('Questions sheet is empty');
  }

  const templateRow = templateRows[0];

  const templateName =
    templateRow['Template Name']?.toString().trim() || 'Untitled Template';

  const description = templateRow.Description?.toString().trim() || '';

  const applicability = templateRow.Applicability?.toString().trim() || 'Both';

  const sectionsMap = new Map<string, Map<string, ParsedQuestion[]>>();

  questionRows.forEach((row) => {
    const sectionName = row.Section?.toString().trim() || 'New Section';

    const subsectionName =
      row.Subsection?.toString().trim() || 'New Subsection';

    const questionText = row['Question Text']?.toString().trim() || '';

    const questionType = row.Type?.toString().trim() || 'ShortText';

    const optionsString = row.Options?.toString().trim() || '';

    const isRequired =
      row['Is Required']?.toString().trim().toLowerCase() === 'true' ||
      row['Is Required']?.toString().trim().toLowerCase() === 'yes';

    // Ignore empty rows
    if (!questionText) {
      return;
    }

    const options = optionsString
      ? optionsString
          .split('@@')
          .map((opt: string) => opt.trim())
          .filter(Boolean)
      : [];

    // Validate select questions
    if (
      (questionType === 'SelectOne' || questionType === 'SelectMany') &&
      options.length === 0
    ) {
      throw new Error(`Question "${questionText}" must have options`);
    }

    // Create section
    if (!sectionsMap.has(sectionName)) {
      sectionsMap.set(sectionName, new Map());
    }

    const subsectionsMap = sectionsMap.get(sectionName)!;

    // Create subsection
    if (!subsectionsMap.has(subsectionName)) {
      subsectionsMap.set(subsectionName, []);
    }

    // Add question
    subsectionsMap.get(subsectionName)!.push({
      id: generateQuestionId(sectionName, subsectionName, questionText),
      text: questionText,
      type: questionType,
      options,
      isRequired,
    });
  });

  const sections: ParsedSection[] = [];

  sectionsMap.forEach((subsectionsMap, sectionName) => {
    const subsections: ParsedSubsection[] = [];

    subsectionsMap.forEach((questions, subsectionName) => {
      subsections.push({
        title: subsectionName,
        questions,
      });
    });

    sections.push({
      title: sectionName,
      subsections,
    });
  });

  return {
    name: templateName,
    description,
    applicability: validateApplicability(applicability),
    sections,
  };
};

export const parseExcelToTemplate = async (
  file: File
): Promise<ParsedTemplate> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const result = event.target?.result;

        if (!result) {
          reject(new Error('Unable to read Excel file'));
          return;
        }

        const workbook = XLSX.read(result, {
          type: 'array',
        });

        const templatesSheetName = 'Templates';

        const templatesSheet = workbook.Sheets[templatesSheetName];

        if (!templatesSheet) {
          reject(new Error('Templates sheet not found in Excel file'));
          return;
        }
        const title = String(templatesSheet.A1?.v ?? '').trim();
        const description = String(templatesSheet.B1?.v ?? '').trim();
        const type = String(templatesSheet.C1?.v ?? '').trim();

        if (!title) {
          reject(new Error('Template title is required'));
          return;
        }

        if (!description) {
          reject(new Error('Template description is required'));
          return;
        }

        if (!type) {
          reject(new Error('Assessment type is required'));
          return;
        }

        const questionsSheetName = 'Questions';
        const questionsSheet = workbook.Sheets[questionsSheetName];

        const section = String(questionsSheet.A1 ?? '').trim();
        const subsection = String(questionsSheet.B1 ?? '').trim();
        const questionText = String(questionsSheet.C1 ?? '').trim();
        const qtype = String(questionsSheet.D1 ?? '').trim();
        const options = String(questionsSheet.E1 ?? '').trim();

        if (!section) {
          throw new Error(`Section is required`);
        }

        if (!subsection) {
          throw new Error(`Subsection is required`);
        }

        if (!questionText) {
          throw new Error(`Question Text is required`);
        }

        if (!qtype) {
          throw new Error(`Question Type is required`);
        }

        if ((qtype === 'SelectOne' || qtype === 'SelectMany') && !options) {
          throw new Error(`Options are required `);
        }

        if (!questionsSheet) {
          reject(new Error('Questions sheet not found in Excel file'));
          return;
        }

        const templateRows = XLSX.utils.sheet_to_json<Record<string, any>>(
          templatesSheet,
          {
            defval: '',
          }
        );

        const questionRows = XLSX.utils.sheet_to_json<Record<string, any>>(
          questionsSheet,
          {
            defval: '',
          }
        );

        if (!templateRows.length) {
          reject(new Error('Templates sheet is empty'));
          return;
        }

        if (!questionRows.length) {
          reject(new Error('Questions sheet is empty'));
          return;
        }

        const parsedTemplate = parseTemplateData(templateRows, questionRows);

        resolve(parsedTemplate);
      } catch (error) {
        console.error('Excel parsing error:', error);

        reject(
          error instanceof Error
            ? error
            : new Error('Failed to parse Excel file. Please check the format.')
        );
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read the file'));
    };

    reader.readAsArrayBuffer(file);
  });
